import { useEffect, useState } from "react";
import Dataset from "datasets/types/interfaces/dataset";
import { Database, MessageSquareOff } from "lucide-react";
import { cn } from "~/lib/utils";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { Agent } from "~/services/agents";
import { createProjectConversation } from "~/services/projects";

interface NewConversationDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachedAgents: Agent[];
  attachedDatasets: Dataset[];
  onCreated?: (conversationId: string) => void;
}

export const NewConversationDialog = ({
  projectId,
  open,
  onOpenChange,
  attachedAgents,
  attachedDatasets,
  onCreated,
}: NewConversationDialogProps) => {
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedAgentId(attachedAgents.length === 1 ? attachedAgents[0].id : null);
      setSelectedDatasetId(attachedDatasets.length === 1 ? attachedDatasets[0].id : null);
    }
  }, [open, attachedAgents, attachedDatasets]);

  const { execute, isPending } = useAsyncMutation(
    ({ agentId, datasetId }: { agentId: string; datasetId: string }) =>
      createProjectConversation(projectId, {
        agent_id: agentId,
        dataset_id: datasetId,
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    {
      errorMessage: "Failed to start conversation",
      onSuccess: (conversation) => {
        onCreated?.(conversation.id);
        onOpenChange(false);
      },
    }
  );

  const hasAgents = attachedAgents.length > 0;
  const hasDatasets = attachedDatasets.length > 0;
  const canStart = hasAgents && hasDatasets && !!selectedAgentId && !!selectedDatasetId;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="New conversation"
      description="Pick an agent and dataset to start a conversation with."
      contentClassName="sm:max-w-[560px]"
      onPrimary={() =>
        selectedAgentId &&
        selectedDatasetId &&
        void execute({ agentId: selectedAgentId, datasetId: selectedDatasetId })
      }
      isPending={isPending}
      primaryLabel="Start"
      primaryPendingLabel="Starting…"
      primaryDisabled={!canStart}
    >
      {hasAgents ? (
        <div className="flex flex-col gap-4">
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Agent
            </p>
            <ul className="grid max-h-[30vh] grid-cols-3 gap-2 overflow-y-auto p-1" role="list">
              {attachedAgents.map((agent) => (
                <AgentOption
                  key={agent.id}
                  agent={agent}
                  selected={selectedAgentId === agent.id}
                  onSelect={() => setSelectedAgentId(agent.id)}
                />
              ))}
            </ul>
          </div>
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Dataset
            </p>
            {hasDatasets ? (
              <ul className="flex max-h-[20vh] flex-col gap-1 overflow-y-auto p-1" role="list">
                {attachedDatasets.map((dataset) => (
                  <DatasetOption
                    key={dataset.id}
                    dataset={dataset}
                    selected={selectedDatasetId === dataset.id}
                    onSelect={() => setSelectedDatasetId(dataset.id)}
                  />
                ))}
              </ul>
            ) : (
              <div role="status" className="flex flex-col items-center gap-2 px-2 py-4 text-center">
                <Database className="size-6 text-muted-foreground" aria-hidden="true" />
                <p className="text-xs text-muted-foreground">
                  Attach a dataset to this project before starting a conversation.
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div role="status" className="flex flex-col items-center gap-2 px-2 py-8 text-center">
          <MessageSquareOff className="size-8 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm font-medium text-foreground">No agents attached</p>
          <p className="max-w-xs text-xs text-muted-foreground">
            Attach an agent to this project before starting a conversation.
          </p>
        </div>
      )}
    </FormDialog>
  );
};

interface AgentOptionProps {
  agent: Agent;
  selected: boolean;
  onSelect: () => void;
}

const AgentOption = ({ agent, selected, onSelect }: AgentOptionProps) => (
  <li>
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex w-full flex-col items-center gap-2 rounded-lg border border-transparent px-2 py-3",
        "ring-1 ring-border/60 transition-all",
        "hover:bg-accent/40 hover:ring-border",
        selected && "bg-accent/40 ring-2 ring-primary hover:ring-primary"
      )}
    >
      <AgentAvatar id={agent.id} name={agent.name} size={48} />
      <p className="line-clamp-2 text-center text-xs font-medium text-foreground">
        {agent.display_name}
      </p>
    </button>
  </li>
);

interface DatasetOptionProps {
  dataset: Dataset;
  selected: boolean;
  onSelect: () => void;
}

const DatasetOption = ({ dataset, selected, onSelect }: DatasetOptionProps) => (
  <li>
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex w-full items-center gap-2 rounded-md border border-transparent px-3 py-2",
        "ring-1 ring-border/60 transition-all text-left",
        "hover:bg-accent/40 hover:ring-border",
        selected && "bg-accent/40 ring-2 ring-primary hover:ring-primary"
      )}
    >
      <Database className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <p className="line-clamp-1 text-xs font-medium text-foreground">{dataset.title}</p>
    </button>
  </li>
);
